import { difficultyScoreMultiplier, MAX_POINTS, noPointsNoteTypes } from '~/consts';
import { DetailedScore, Note, NotesSection, PlayerNote, songBeat, SongTrack } from '~/interfaces';
import { comboMultiplierByNote } from '~/modules/game-engine/game-state/helpers/combo';
import getPlayerNoteDistance from '~/modules/game-engine/helpers/get-player-note-distance';
import isNotesSection from '~/modules/songs/utils/is-notes-section';
import tuple from '~/modules/utils/tuple';

export { MAX_POINTS };

const noteTypesMultipliers: DetailedScore = {
  freestyle: 0.25,
  rap: 0.25,
  rapstar: 0.5,
  star: 2,
  normal: 1,
  perfect: 0.5,
  vibrato: 0.25,
};

const countsToBeats = (counts: DetailedScore): DetailedScore => ({
  freestyle: counts.freestyle * noteTypesMultipliers.freestyle,
  rap: counts.rap * noteTypesMultipliers.rap,
  rapstar: counts.rapstar * noteTypesMultipliers.rapstar,
  star: counts.star * noteTypesMultipliers.star,
  normal: counts.normal * noteTypesMultipliers.normal,
  perfect: counts.perfect * noteTypesMultipliers.perfect,
  vibrato: counts.vibrato * noteTypesMultipliers.vibrato,
});

/** A score of nothing - for a player who has no `PlayerState` to score at all. */
export const emptyDetailedScore = (): DetailedScore => ({
  freestyle: 0,
  rap: 0,
  rapstar: 0,
  star: 0,
  normal: 0,
  perfect: 0,
  vibrato: 0,
});

/**
 * `rapstar` belongs here with the rest. Leaving it out meant golden rap notes counted towards
 * neither the maximum nor the player's total, so hitting them earned nothing while every other note
 * was quietly worth proportionally more - and it is the only one of these operations that skipped
 * it. 68 of the shipped songs have such notes.
 */
export const sumDetailedScore = (counts: DetailedScore) =>
  counts.freestyle + counts.rap + counts.rapstar + counts.star + counts.normal + counts.perfect + counts.vibrato;

export const beatsToPoints = (counts: DetailedScore, pointsPerBeat: number): DetailedScore => ({
  freestyle: counts.freestyle * pointsPerBeat,
  rap: counts.rap * pointsPerBeat,
  rapstar: counts.rapstar * pointsPerBeat,
  star: counts.star * pointsPerBeat,
  normal: counts.normal * pointsPerBeat,
  perfect: counts.perfect * pointsPerBeat,
  vibrato: counts.vibrato * pointsPerBeat,
});

export const addDetailedScores = (s1: DetailedScore, s2: DetailedScore): DetailedScore => ({
  freestyle: s1.freestyle + s2.freestyle,
  rap: s1.rap + s2.rap,
  rapstar: s1.rapstar + s2.rapstar,
  star: s1.star + s2.star,
  normal: s1.normal + s2.normal,
  perfect: s1.perfect + s2.perfect,
  vibrato: s1.vibrato + s2.vibrato,
});

export const divideDetailedScores = (counts: DetailedScore, divideBy: number): DetailedScore => ({
  freestyle: counts.freestyle / divideBy,
  rap: counts.rap / divideBy,
  rapstar: counts.rapstar / divideBy,
  star: counts.star / divideBy,
  normal: counts.normal / divideBy,
  perfect: counts.perfect / divideBy,
  vibrato: counts.vibrato / divideBy,
});

/** Whether a section was this singer's to sing. Absent means the whole track was. */
type OwnsSection = (section: NotesSection) => boolean;

// `WeakMap`s rather than memo caches, so what a track's beats came to is forgotten when the song
// holding that track is. Keyed on the track and on the sections, and read on every score, which is
// every frame the score is on screen - a plain cache kept every track of every song played all
// session.
const sungBeatsByTrack = new WeakMap<SongTrack, DetailedScore>();
const sungBeatsBySection = new WeakMap<NotesSection, DetailedScore>();
const sectionByNoteStart = new WeakMap<SongTrack, Map<songBeat, NotesSection>>();

const addNoteBeats = (counts: DetailedScore, notes: Note[]) => {
  notes
    .filter((note) => !noPointsNoteTypes.includes(note.type))
    .forEach((note) => {
      counts[note.type] = counts[note.type] + note.length;
      counts.perfect = counts.perfect + note.length;
      counts.vibrato = counts.vibrato + note.length;
    });

  return counts;
};

// Per section rather than per track, so pass the mic can add up the sections one singer owns
// without walking their notes again every frame.
const countSectionBeats = (section: NotesSection): DetailedScore => {
  const known = sungBeatsBySection.get(section);
  if (known) return known;

  const counts = addNoteBeats(emptyDetailedScore(), section.notes);
  sungBeatsBySection.set(section, counts);

  return counts;
};

const sumSections = (sections: NotesSection[]) =>
  sections.reduce((total, section) => addDetailedScores(total, countSectionBeats(section)), emptyDetailedScore());

const countSungBeats = (track: SongTrack, ownsSection?: OwnsSection): DetailedScore => {
  const sections = track.sections.filter(isNotesSection);

  if (ownsSection) return sumSections(sections.filter(ownsSection));

  const known = sungBeatsByTrack.get(track);
  if (known) return known;

  const counts = sumSections(sections);
  sungBeatsByTrack.set(track, counts);

  return counts;
};

/**
 * Which section each note of the track belongs to, by its start beat - the same identity
 * `calculate-note-stats` ties a sung note to its track note with. Built once per track, because the
 * alternative is walking every note of the song on every frame of every singer's score.
 */
const sectionsOfTrack = (track: SongTrack) => {
  const known = sectionByNoteStart.get(track);
  if (known) return known;

  const byStart = new Map<songBeat, NotesSection>();
  track.sections
    .filter(isNotesSection)
    .forEach((section) => section.notes.forEach((note) => byStart.set(note.start, section)));
  sectionByNoteStart.set(track, byStart);

  return byStart;
};

// Scored against the track object the singer actually sang, not a track index. Any player count but
// two reads the merged track (`isMergedTrack`), so indexing `song.tracks` here counted the sung
// beats of the merged track against the maximum of one original track - a solo run on a duet scored
// double the ceiling, and at three or four singers each got a different denominator for the same
// material.
//
// `ownsSection` narrows both halves of the sum together - pass the mic asks a singer for their own
// turns and nothing else, so what they earned and what they could have earned describe the same
// material. Narrowing only the maximum would let a singer who sang through everyone's turn score
// more than the whole of their own part.
//
// Vibrato is left out of the maximum: most singers never produce it, so counting it there capped a
// round sung perfectly at 86%. It scores on top instead, as do the combo and the difficulty.
export function calculateDetailedScoreData(
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  ownsSection?: OwnsSection,
) {
  const counts = emptyDetailedScore();
  const maxCounts = countsToBeats(track ? countSungBeats(track, ownsSection) : emptyDetailedScore());
  const maxBeats = sumDetailedScore(maxCounts) - maxCounts.vibrato;

  // Nothing to sing awards nothing, rather than dividing by zero and scoring every note Infinity.
  if (maxBeats === 0) {
    return tuple([0, counts, maxCounts]);
  }

  const pointsPerBeat = (MAX_POINTS * difficultyScoreMultiplier(tolerance)) / maxBeats;
  const comboMultipliers = comboMultiplierByNote(playerNotes, track, tolerance, { ownsSection });
  const sections = track && ownsSection ? sectionsOfTrack(track) : null;
  // A note the track does not hold contributed nothing to the maximum above, so counting it here
  // would put the score over its own ceiling. Only reachable if a singer's notes were captured
  // against a different track from the one they are being scored on.
  const wasTheirs = (note: PlayerNote) => {
    if (!sections || !ownsSection) return true;

    const section = sections.get(note.note.start);

    return section !== undefined && ownsSection(section);
  };

  for (let i = 0; i < playerNotes.length; i++) {
    const note = playerNotes[i];
    if (noPointsNoteTypes.includes(note.note.type)) continue;
    if (getPlayerNoteDistance(note) !== 0) continue;
    if (!wasTheirs(note)) continue;

    const beats = note.length * (comboMultipliers.get(note.note.start) ?? 1);
    counts[note.note.type] = counts[note.note.type] + beats;

    if (note.isPerfect) counts.perfect = counts.perfect + beats;
    if (note.vibrato) counts.vibrato = counts.vibrato + beats;
  }

  return tuple([pointsPerBeat, countsToBeats(counts), maxCounts]);
}

export default function calculateScore(
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  ownsSection?: OwnsSection,
): number {
  const [pointsPerBeat, counts] = calculateDetailedScoreData(playerNotes, track, tolerance, ownsSection);

  return sumDetailedScore(counts) * pointsPerBeat;
}
